import { useState, useEffect } from 'react';
import ModalBase from '@/components/base/ModalBase';
import { SwalAlert } from '@/hooks/useSweetAlert';
import type { StaffMember, StaffRole, StaffPermission } from '@/mocks/adminStaff';
import { roleLabels, roleDescriptions, permissionLabels, defaultRolePermissions } from '@/mocks/adminStaff';

export interface StaffFormData {
  name: string;
  role: StaffRole;
  email: string;
  bio: string;
  photoUrl: string;
  instagram: string;
  twitter: string;
  youtube: string;
  tiktok: string;
  website: string;
  assignedPrograms: string;
  permissions: StaffPermission[];
}

interface StaffFormModalProps {
  open: boolean;
  member: StaffMember | null;
  mode: 'invite' | 'edit';
  onClose: () => void;
  onSave: (data: StaffFormData) => void;
}

const defaultForm: StaffFormData = {
  name: '',
  role: 'locutor',
  email: '',
  bio: '',
  photoUrl: '',
  instagram: '',
  twitter: '',
  youtube: '',
  tiktok: '',
  website: '',
  assignedPrograms: '',
  permissions: [],
};

const allRoles: StaffRole[] = ['administrador', 'locutor', 'moderador', 'marketing', 'ventas', 'invitado'];

const allPermissions: StaffPermission[] = ['cabina', 'chat', 'contenido', 'monetizacion', 'analytics', 'configuracion'];

export default function StaffFormModal({ open, member, mode, onClose, onSave }: StaffFormModalProps) {
  const [form, setForm] = useState<StaffFormData>(defaultForm);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (member && mode === 'edit') {
      setForm({
        name: member.name,
        role: member.role,
        email: member.email,
        bio: member.bio,
        photoUrl: member.photoUrl,
        instagram: member.socialLinks.instagram || '',
        twitter: member.socialLinks.twitter || '',
        youtube: member.socialLinks.youtube || '',
        tiktok: member.socialLinks.tiktok || '',
        website: member.socialLinks.website || '',
        assignedPrograms: member.assignedPrograms.join(', '),
        permissions: [...member.permissions],
      });
    } else {
      const initialRole: StaffRole = 'locutor';
      setForm({
        ...defaultForm,
        role: initialRole,
        permissions: [...defaultRolePermissions[initialRole]],
      });
    }
    setFieldErrors({});
  }, [member, mode, open]);

  const update = (field: keyof StaffFormData, value: string | StaffRole | StaffPermission[]) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'role' && mode === 'invite') {
        next.permissions = [...defaultRolePermissions[value as StaffRole]];
      }
      return next;
    });
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const togglePermission = (perm: StaffPermission) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter((p) => p !== perm)
        : [...prev.permissions, perm],
    }));
  };

  const validate = (): string | null => {
    if (!form.email.trim()) return 'El email es requerido.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'El formato del email no es válido.';
    if (mode === 'invite' && !form.name.trim()) return 'El nombre es requerido.';
    if (mode === 'edit' && !form.name.trim()) return 'El nombre es requerido.';
    return null;
  };

  const handleSubmit = async () => {
    const error = validate();
    if (error) {
      setFieldErrors({
        name: !form.name.trim() ? 'name' : '',
        email: !form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) ? 'email' : '',
      });
      await SwalAlert.warning('Validación', error);
      return;
    }
    onSave(form);
  };

  const isInviteMode = mode === 'invite';
  const selectedRoleDesc = roleDescriptions[form.role];

  return (
    <ModalBase
      open={open}
      onClose={onClose}
      title={isInviteMode ? 'Invitar al Staff' : 'Editar Miembro'}
      subtitle={isInviteMode
        ? 'Enviá una invitación por email. Al aceptar, el usuario podrá acceder con los permisos de su rol.'
        : 'Modificá los datos, rol y permisos del miembro.'}
      size="xl"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-foreground-600 bg-background-100 hover:bg-background-200 cursor-pointer transition-colors whitespace-nowrap"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 rounded-xl text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 cursor-pointer transition-colors whitespace-nowrap"
          >
            {isInviteMode ? (
              <span className="flex items-center justify-center gap-1.5">
                <i className="ri-send-plane-line text-sm"></i>
                Enviar Invitación
              </span>
            ) : (
              'Guardar Cambios'
            )}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Email */}
        <div>
          <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
            Email <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all ${
                fieldErrors.email ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
              }`}
              placeholder="invitado@email.com"
            />
          </div>
          {isInviteMode && (
            <p className="text-[10px] text-foreground-400 mt-1">Se enviará un email con instrucciones para unirse al equipo.</p>
          )}
        </div>

        {/* Name + Role */}
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          <div className="sm:col-span-3">
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Nombre completo <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              className={`w-full px-3.5 py-2.5 rounded-xl border text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all ${
                fieldErrors.name ? 'border-red-400 bg-red-50/50' : 'border-background-200 bg-background-50'
              }`}
              placeholder="Ej. Valentina Ríos"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
              Rol <span className="text-red-500">*</span>
            </label>
            <select
              value={form.role}
              onChange={(e) => update('role', e.target.value as StaffRole)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all cursor-pointer"
            >
              {allRoles.map((r) => (
                <option key={r} value={r}>{roleLabels[r]}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Role description */}
        <div className="bg-background-100 rounded-xl p-3 flex items-start gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-500 text-sm"></i>
          </div>
          <div>
            <p className="text-[11px] font-semibold text-foreground-700 mb-0.5">{roleLabels[form.role]}</p>
            <p className="text-[10px] text-foreground-500 leading-relaxed">{selectedRoleDesc}</p>
          </div>
        </div>

        {/* Bio */}
        <div>
          <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
            Biografía
          </label>
          <textarea
            value={form.bio}
            onChange={(e) => update('bio', e.target.value)}
            rows={2}
            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all resize-none"
            placeholder="Una breve descripción..."
            maxLength={500}
          />
          <p className="text-[10px] text-foreground-400 mt-1 text-right">{form.bio.length}/500</p>
        </div>

        {/* Programs */}
        <div>
          <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-1.5">
            Programas asignados
          </label>
          <input
            type="text"
            value={form.assignedPrograms}
            onChange={(e) => update('assignedPrograms', e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:ring-2 focus:ring-primary-400/60 transition-all"
            placeholder="Separar con comas, ej: El Mañanero, La Hora Pico"
          />
        </div>

        {/* Permissions */}
        <div>
          <label className="block text-[11px] font-bold text-foreground-600 uppercase tracking-wider mb-2">
            Permisos de acceso
          </label>
          <div className="bg-background-100 rounded-2xl p-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {allPermissions.map((perm) => {
                const active = form.permissions.includes(perm);
                return (
                  <button
                    key={perm}
                    type="button"
                    onClick={() => togglePermission(perm)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-[11px] font-bold cursor-pointer transition-all whitespace-nowrap border ${
                      active
                        ? 'bg-primary-500 text-background-50 border-primary-500'
                        : 'bg-background-50 text-foreground-500 border-background-200 hover:border-foreground-300'
                    }`}
                  >
                    <i className={`text-xs ${active ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}`}></i>
                    {permissionLabels[perm]}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-background-200/70 flex items-center justify-between">
              <span className="text-[10px] text-foreground-400">
                {form.permissions.length} de {allPermissions.length} permisos activos
              </span>
              {form.permissions.length > 0 && (
                <button
                  type="button"
                  onClick={() => update('permissions', [])}
                  className="text-[10px] font-semibold text-red-500 hover:text-red-600 cursor-pointer transition-colors whitespace-nowrap"
                >
                  Limpiar todos
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </ModalBase>
  );
}